import { useEffect, useRef } from "react";

export function isChanged(prop: any, nextProp: any) {
  return prop !== nextProp;
}

/**
 * Lifecycle-shadowing hooks for class -> function component conversions
 * (see PR #1829's description for the full migration plan).
 *
 * These deliberately mirror class lifecycle semantics as closely as
 * possible instead of translating straight to idiomatic dependency-array
 * `useEffect`s. That's the point: converting a class's manual prop-diffing
 * `componentDidUpdate` into a *correct* dependency array is a real judgment
 * call and the highest-risk part of any single conversion. Using these
 * keeps a conversion a near-mechanical transliteration, so behavior can't
 * drift from the class version. Refactoring a given file to idiomatic
 * `useEffect`s (once it's proven stable) is a deliberate later cleanup
 * pass, not something to fold into the same change as the conversion.
 */

/** Mirrors `componentDidMount`: runs `effect` once, after the first render. */
export function useDidMount(effect: () => void): void {
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(effect, []);
}

/**
 * Mirrors `componentWillUnmount`: runs `cleanup` once, when the component
 * unmounts. `cleanup` is read through a ref so the version from the final
 * render runs, even though the effect that registers it only fires once.
 */
export function useWillUnmount(cleanup: () => void): void {
  const cleanupRef = useRef(cleanup);
  cleanupRef.current = cleanup;
  useEffect(() => {
    return () => cleanupRef.current();
  }, []);
}

/**
 * Mirrors `componentDidUpdate(prevProps, prevState)`: runs `effect` after
 * every render except the first, passing the snapshot of `current` that was
 * captured on the *previous* render. `current` is typically `props`, or
 * `props` combined with whatever local state the original method diffed
 * against — bundle whatever the class read off `prevProps`/`prevState`.
 *
 * Deliberately has no dependency array, matching the class method's actual
 * behavior of firing after every completed update, not just some of them.
 * Do your own diffing inside `effect` (e.g. with `isChanged`), exactly as
 * the class did — this hook doesn't decide when to skip firing.
 */
export function useDidUpdate<T>(current: T, effect: (prev: T) => void): void {
  const isFirstRender = useRef(true);
  const prevRef = useRef(current);

  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
    } else {
      effect(prevRef.current);
    }
    prevRef.current = current;
  });
}
